import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import "../../styles/modules.css";

// Displays a force-directed graph showing FDA 510(k) submission relationships.

// interface NodeData {
//   id: string;
//   x?: number;
//   y?: number;
//   vx?: number;
//   vy?: number;
//   fx?: number | null;
//   fy?: number | null;
// }

// interface LinkData {
//   source: string | NodeData;
//   target: string | NodeData;
// }

// interface GraphData {
//   nodes: NodeData[];
//   links: LinkData[];
// }

// interface ForceGraphProps {
//   width?: number;
//   height?: number;
//   data?: GraphData;
//   onBubbleClick?: (nodeId: string) => void;
// }

const PredicateForceGraph/*: React.FC<ForceGraphProps>*/ = ({ width = 900, height = 600, data, onBubbleClick }) => {
  const svgRef = useRef/*<SVGSVGElement | null>*/(null);

  const sample/*: GraphData*/ = {
    nodes: [
      { id: "K230001" },
      { id: "K230234" },
      { id: "K231111" },
      { id: "K240500" },
      { id: "K241234" },
      { id: "K242000" },
      { id: "K245678" },
      { id: "K249999" },
    ],
    links: [
      { source: "K230001", target: "K231111" },
      { source: "K230234", target: "K231111" },
      { source: "K231111", target: "K240500" },
      { source: "K240500", target: "K241234" },
      { source: "K241234", target: "K242000" },
      { source: "K242000", target: "K245678" },
      { source: "K230001", target: "K245678" },
      { source: "K245678", target: "K249999" },
    ],
  };

  const convertSearchResultsToGraphData = (searchResults/*: any[]*/)/*: GraphData*/ => {
    const nodesMap = new Map();
    const links = [];
    const existingKNumbers = new Set();
    const extraPredicates = new Set();

    // Step 1: create nodes for products (green)
    searchResults.forEach((product) => {
      const kNumber = product.kNumber;
      if (kNumber) {
      nodesMap.set(kNumber, { id: kNumber, color: "green" });
      existingKNumbers.add(kNumber);
      }
    });

    // Step 2: create links and collect predicate k-numbers that are not in products
    searchResults.forEach((product) => {
      const kNumber = product.kNumber;
      const primary = product.primaryPredicateKNumber;
      if (!primary) return;

      // handle possible comma-separated list or single value
      const predicateKNumbers = String(primary)
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

      predicateKNumbers.forEach((predicateK) => {
      if (kNumber) {
        // product -> predicate (follow TypeScript example)
        links.push({ source: kNumber, target: predicateK });
      }
      if (!existingKNumbers.has(predicateK)) {
        extraPredicates.add(predicateK);
      }
      });
    });

    // Step 3: add missing predicate nodes as gray
    extraPredicates.forEach((kNumber) => {
      if (!nodesMap.has(kNumber)) {
      nodesMap.set(kNumber, { id: kNumber, color: "gray" });
      }
    });

    return {
      nodes: Array.from(nodesMap.values()),
      links,
    };
  };

  useEffect(() => {
    if (!data) return;

    const graph = convertSearchResultsToGraphData(data) || sample;

    const svg = d3.select(svgRef.current);
    svg.attr("id", "predicate-graph").attr("overflow", "visible");
    svg.selectAll("*").remove();

    const w = width;
    const h = height;

    
    // create zoom behavior, call it, then apply an initial transform
    const zoom = d3
      .zoom()
      .scaleExtent([0.2, 3])
      .on("zoom", (event) => {
        container.attr("transform", event.transform);
      });

    svg.call(zoom);

    // choose initial scale and center the graph (adjust values as needed)
    const initialScale = 0.5;
    const tx = (w - w * initialScale) / 2;
    const ty = (h - h * initialScale) / 2;

    // apply the initial transform (use transition duration 0 to set immediately)
    svg.transition().duration(0).call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(initialScale));


    const nCount = graph.nodes.length;
    const baseR = nCount < 50 ? 28 : 24;

    const defs = svg.append("defs");

    defs
      .append("marker")
      .attr("id", "arrow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", baseR + 4)
      .attr("refY", 0)
      .attr("markerWidth", 7)
      .attr("markerHeight", 7)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#999");

    const filter = defs.append("filter").attr("id", "dropshadow").attr("height", "130%");

    filter.append("feGaussianBlur").attr("in", "SourceAlpha").attr("stdDeviation", 2).attr("result", "blur");

    filter.append("feOffset").attr("in", "blur").attr("dx", 1).attr("dy", 1).attr("result", "offsetBlur");

    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "offsetBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    const container = svg
      .attr("viewBox", `0 0 ${w} ${h}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .append("g")
      .attr("class", "container");

    svg.call(
      d3
        .zoom/*<SVGSVGElement, unknown>*/()
        .scaleExtent([0.2, 3])
        .on("zoom", (event) => {
          container.attr("transform", event.transform);
        }),
    );

    const simulation = d3
      .forceSimulation/*<NodeData>*/(graph.nodes)
      .force(
        "link",
        d3
          .forceLink/*<NodeData, LinkData>*/(graph.links)
          .id((d) => d.id)
          .distance(120)
          .strength(0.6),
      )
      .force("charge", d3.forceManyBody().strength(-100).distanceMax(100))
      .force("center", d3.forceCenter(w / 2, h / 2))
      .force(
        "collision",
        d3.forceCollide/*<NodeData>*/().radius(() => baseR + 6),
      )
      .alphaTarget(0.1);

    const link = container
      .append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(graph.links)
      .enter()
      .append("line")
      .attr("stroke", "#999")
      .attr("stroke-opacity", 0.6)
      .attr("stroke-width", 2)
      //.attr("marker-end", "url(#arrow)");
      //.attr("marker-end", "url(#predicate-graph#arrow)");
      .attr("marker-end", `url(${window.location.href}#arrow)`);

    const node = container
      .append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(graph.nodes)
      .enter()
      .append("g")
      .call(
        d3
          .drag/*<SVGGElement, NodeData>*/()
          .on("start", (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on("end", (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }),
      );

    node
      .append("circle")
      .attr("r", baseR)
      //.attr("fill", (_, i) => d3.schemeTableau10[i % 10])
      //.attr("fill", "#038C5A")
      .attr("fill", (d) => d.color)
      .attr("stroke", "#333")
      .attr("stroke-width", 1)
      .attr("filter", "url(#dropshadow)")
      .style("cursor", "pointer")
      .on("click", function (event, d) {
        if (onBubbleClick) {
          onBubbleClick(d.id);
        }
      })
      .on("mouseover", function (event, d) {
        d3.select(this)
          .transition()
          .attr("r", baseR * 1.15);
        tooltip.style("opacity", 1).html(`<strong>${d.id}</strong>`);
      })
      .on("mousemove", function (event) {
        tooltip.style("left", event.pageX + 12 + "px").style("top", event.pageY + 12 + "px");
      })
      .on("mouseout", function () {
        d3.select(this).transition().attr("r", baseR);
        tooltip.style("opacity", 0);
      });

    node
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", ".35em")
      .attr("pointer-events", "none")
      .style("font-family", "monospace")
      .style("font-size", "12px")
      .style("fill", "#fff")
      .text((d) => d.id);

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("padding", "6px 8px")
      .style("background", "rgba(0,0,0,0.75)")
      .style("color", "#fff")
      .style("border-radius", "4px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0);

    simulation.on("tick", () => {
      link
        .attr("x1", (d) => (d.source/* as NodeData*/).x || 0)
        .attr("y1", (d) => (d.source/* as NodeData*/).y || 0)
        .attr("x2", (d) => (d.target/* as NodeData*/).x || 0)
        .attr("y2", (d) => (d.target/* as NodeData*/).y || 0);

      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
      tooltip.remove();
    };
  }, [data, width, height, onBubbleClick]);

  return (
    <div className="bubble-map-tile" style={{ overflow: "hidden" }}>
      <svg
        ref={svgRef}
        style={{ width: "100%", height, display: "block" }}
      />
    </div>
  );
};

export default PredicateForceGraph;
